import React, { useEffect, useMemo, useState } from "react";
import { EmployerinformationI, EmployerSelectValue } from "../interfaces";
import { getAllEmployer } from "../redux/selfService/selfServiceSlice";
import Select from "react-select";
import { useAppDispatch } from "../redux/hooks";

interface PropsI {
  onChange: (e: EmployerSelectValue | null) => void;
  defaultValue?: EmployerSelectValue | null;
}

const EmployerSelect = ({ onChange, defaultValue }: PropsI) => {
  const dispatch = useAppDispatch();
  const [inputString, setInputString] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [options, setOptions] = useState<EmployerSelectValue[]>([]);
  const [selectedValue, setSelectedValue] = useState<EmployerSelectValue | null>(
    defaultValue || null
  );

  useEffect(() => {
    setSelectedValue(defaultValue || null);
    if (defaultValue?.label) {
      setOptions((prev) => {
        const exists = prev.some(
          (o) =>
            (o.value as EmployerinformationI)?.id ===
            (defaultValue.value as EmployerinformationI)?.id
        );
        return exists ? prev : [defaultValue, ...prev];
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    const timeOutId = setTimeout(async () => {
      if (!inputString || inputString.length < 2) return;
      setSearchLoading(true);
      try {
        const result = await dispatch(getAllEmployer(inputString));
        const payload = (result as any)?.payload;
        const list: EmployerinformationI[] = Array.isArray(payload) ? payload : [];
        const mapped: EmployerSelectValue[] = list.map((emp) => ({
          value: emp,
          label: emp.txtemployername,
        }));
        setOptions((prev) => {
          const selected = selectedValue ? [selectedValue] : [];
          const merged = [...selected, ...mapped];
          const seen = new Set<number | string>();
          return merged.filter((o) => {
            const id = (o.value as EmployerinformationI)?.id ?? o.label;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
        });
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeOutId);
  }, [inputString, dispatch, selectedValue]);

  const handleSelectChange = (selectedOption: EmployerSelectValue | null) => {
    setSelectedValue(selectedOption);
    onChange(selectedOption);
  };

  const selectOptions = useMemo(() => options, [options]);

  return (
    <Select
      id="employer"
      className="react-select-container"
      classNamePrefix="react-select"
      isLoading={searchLoading}
      isClearable
      isSearchable
      filterOption={() => true}
      options={selectOptions}
      placeholder="Type employer name to search"
      value={selectedValue}
      onChange={handleSelectChange}
      onInputChange={(val, meta) => {
        if (meta.action === "input-change") {
          setInputString(val);
        }
      }}
      noOptionsMessage={({ inputValue }) =>
        inputValue && inputValue.length > 1
          ? "No employers found"
          : "Type at least 2 characters to search"
      }
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
    />
  );
};

export default EmployerSelect;
